"use client";

import { Skeleton } from "@offstreetparking/design-system/feedback";
import { extractLocationLookupId } from "@offstreetparking/domain/entities/location-lookup";
import { CombinedParkingOptionSelection } from "@offstreetparking/portal-flow/components/CombinedParkingOptionSelection";
import { CreatePermitWizard } from "@offstreetparking/portal-flow/components/CreatePermitWizard";
import { PermitStoreProvider } from "@offstreetparking/portal-flow/components/CreatePermitWizard/PermitContextProvider";
import { KioskStartOver } from "@offstreetparking/portal-flow/components/CreatePermitWizard/components/KioskStartOver";
import { PermitTypeUnavailable } from "@offstreetparking/portal-flow/components/PermitTypeUnavailable";
import { SelectionLayout } from "@offstreetparking/portal-flow/components/SelectionLayout";
import { TenantSelection } from "@offstreetparking/portal-flow/components/TenantSelection";
import { ParkingDetailsSkeleton } from "@offstreetparking/portal-flow/components/dataDisplay/parkingDetails";
import { NotFoundAlert, ServerErrorAlert } from "@offstreetparking/portal-flow/components/forms/Alert";
import { ContactSkeleton } from "@offstreetparking/portal-flow/components/layouts/Contact";
import { ContentCard } from "@offstreetparking/portal-flow/components/layouts/ContentCard";
import { PermitFormLayout } from "@offstreetparking/portal-flow/components/layouts/PermitFormLayout";
import { useRouter, useSearchParams } from "next/navigation";
import { use, useEffect, useMemo, useState } from "react";
import { CommonLayout } from "~/components/layouts/CommonLayout";
import { useParker } from "~/components/providers/AuthProvider";
import { KioskProvider } from "~/components/providers/KioskProvider";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { routes } from "~/routes";
import { offstreetClient } from "~/services/offstreetApi";
import { usePortalV2LocationByLookup } from "~/services/portalV2Api";
import { isSettingServableInP2 } from "~/utils/is-setting-servable-in-p2";
import { mergeTenants } from "~/utils/merge-tenants";
import { normalizeRegSettings } from "~/utils/normalize-reg-settings";
import { redirectToPortalV2 } from "~/utils/redirectToP2";

export default function LocationPage(props: { params: Promise<{ locale: string; lookup: string }> }) {
	const params = use(props.params);
	const { lookup } = params;
	const { isPending: flagsPending } = useFeatureFlags();
	const { isInitialLoading: isParkerInitialLoading, parker } = useParker();
	const [selectedTenantId, setSelectedTenantId] = useState<number>();
	const [selectedPermitTypeId, setSelectedPermitTypeId] = useState<number>();
	const [hasMultipleParkingSelection, setHasMultipleParkingSelection] = useState(false);
	const referrerLookupId = extractLocationLookupId(useSearchParams().get("ref") ?? "");
	const router = useRouter();

	const {
		data: lookupData,
		isPending: tRPCPending,
		error: tRPCError,
	} = offstreetClient.locationLookup.getLocationLookupEntitiesByIdentifier.useQuery(
		{
			id: lookup,
			tenantId: selectedTenantId,
			permitTypeId: selectedPermitTypeId,
			referrerLookupId,
		},
		{
			select: (data) => ({
				locationId: data?.locationId,
				tenantId: data?.tenantId,
				selectedTenantId: data?.selectedTenantId,
				permitTypeId: data?.permitTypeId,
				settingId: data?.settingId,
				kioskId: data?.kioskId,
				startOverLookupId: data?.startOverLookupId,
				permitType: data?.permitType,
				permitTypes: data?.permitTypes ?? [],
				tenants: data?.tenants ?? [],
				selectedTenant: data?.tenants?.find((x) => x.id === data?.selectedTenantId),
				location: data?.location,
				branding: data?.branding,
				support: data?.support,
				contact: data?.contact,
				tenantInstructions: data?.tenantInstructions,
				locationInstructions: data?.locationInstructions,
				permitTypeInstructions: data?.permitTypeInstructions,
				overrideNotices: data?.overrideNotices,
			}),
		},
	);

	const isKiosk = lookupData?.kioskId !== undefined;
	const startOverLookupId = lookupData?.startOverLookupId;
	const arrivedFromKioskReferrer = isKiosk && startOverLookupId !== lookup;

	const needsP2Data = !lookupData?.permitTypeId;
	const permitTypeInstructions = lookupData?.permitTypeInstructions?.registrationInstructions;
	const tenantInstructions = lookupData?.tenantInstructions?.registrationInstructions;
	const locationInstructions = lookupData?.locationInstructions?.registrationInstructions;
	const instructions =
		[permitTypeInstructions, tenantInstructions, locationInstructions].map((v) => v?.trim()).find((v) => v) ??
		undefined;

	const {
		data: p2Location,
		isPending: p2Pending,
		error: p2Error,
	} = usePortalV2LocationByLookup({
		lookupId: lookup,
		enabled: needsP2Data,
	});

	const formData = useMemo(() => {
		if (!parker) {
			return undefined;
		}
		return {
			vehicles: parker.defaultVehicle ? [parker.defaultVehicle] : [],
			email: parker.email ?? undefined,
		};
	}, [parker]);
	const hasOverrideNotices = (lookupData?.overrideNotices?.length ?? 0) > 0;

	const effectiveTenantId = selectedTenantId ?? lookupData?.tenantId;
	const p2Tenant = p2Location?.tenants.find((t) => t.id === effectiveTenantId);
	const allRegSettings =
		p2Tenant && p2Location
			? normalizeRegSettings({
					settings: p2Location.registrationSettings,
					tenantRegSettingIds: p2Tenant.registrationSettings ?? [],
					disabledSettings: p2Tenant.disabledSettings,
				})
			: [];

	const totalPermitTypeAndRegSettingDisplayed = (lookupData?.permitTypes ?? []).length + allRegSettings.length;

	// Auto-select logic:
	// - If lookup has permitTypeId → handler already loads full permitType, no action needed
	// - If lookup has settingId → handled below in render (redirect to p2)
	// - If exactly 1 available option total (permit types + reg settings combined):
	//   - If it's a permit type → auto-select in p3
	//   - If it's a reg setting → redirect to p2
	//   - If it's a nav-out overrider → redirect to that permit type's own lookup link
	// - If more than 1 option → show selection UI
	const autoSelectAction = (() => {
		if (lookupData?.permitTypeId || lookupData?.settingId) {
			return undefined;
		}

		if (
			tRPCPending ||
			(needsP2Data && p2Pending) ||
			selectedPermitTypeId ||
			lookupData?.permitTypeId ||
			!effectiveTenantId
		) {
			return undefined;
		}

		const availableRegSettings = allRegSettings.filter((s) => s.isAvailable && !s.isAtCapacity);
		const activePermitTypes = (lookupData?.permitTypes ?? []).filter(
			(pt) => pt.status === "active" && pt.override?.state !== "disabled" && !pt.overrideNavOutLookupId,
		);

		const totalAvailable = activePermitTypes.length + availableRegSettings.length;

		const singleNavOutLookupId =
			totalPermitTypeAndRegSettingDisplayed === 1 && totalAvailable === 0
				? lookupData?.permitTypes?.[0]?.overrideNavOutLookupId
				: undefined;
		if (singleNavOutLookupId) {
			return { type: "overrideNavOut" as const, lookupId: singleNavOutLookupId };
		}

		if (totalPermitTypeAndRegSettingDisplayed !== 1 || totalAvailable !== 1) {
			return undefined;
		}

		if (activePermitTypes.length === 1) {
			return { type: "permitType" as const, id: activePermitTypes[0]!.id };
		}
		if (availableRegSettings.length === 1) {
			return { type: "regSetting" as const, id: availableRegSettings[0]!.id };
		}
		return undefined;
	})();

	useEffect(() => {
		if (!autoSelectAction) {
			return;
		}
		if (autoSelectAction.type === "permitType") {
			setSelectedPermitTypeId(autoSelectAction.id);
			setHasMultipleParkingSelection(totalPermitTypeAndRegSettingDisplayed > 1);
		} else if (autoSelectAction.type === "overrideNavOut") {
			// replace, not push: a back navigation must not land on a page that redirects again
			router.replace(routes.locationLookup({ lookup: autoSelectAction.lookupId, ref: lookup }));
		} else {
			redirectToPortalV2({
				path: `location/${lookup}`,
				settingId: autoSelectAction.id,
				tenantId: selectedTenantId ?? lookupData?.tenantId,
			});
		}
	}, [
		autoSelectAction,
		totalPermitTypeAndRegSettingDisplayed,
		lookup,
		selectedTenantId,
		lookupData?.tenantId,
		router,
	]);

	const isAutoNavigating = autoSelectAction?.type === "regSetting" || autoSelectAction?.type === "overrideNavOut";

	if (flagsPending || tRPCPending || (needsP2Data && p2Pending) || isAutoNavigating || isParkerInitialLoading) {
		return (
			<KioskProvider isKiosk={isKiosk} startOverLookupId={startOverLookupId}>
				<CommonLayout
					infoSlot={
						<div className="space-y-12">
							<div className="space-y-4">
								<Skeleton height={60} width={450} />
								<Skeleton height={60} width={225} />
							</div>

							<ContactSkeleton />
						</div>
					}
					isLoading={true}
				>
					<ContentCard>
						<ParkingDetailsSkeleton />
						<div className="border-neutral-faded border-t p-8 xl:hidden">
							<ContactSkeleton />
						</div>
					</ContentCard>
				</CommonLayout>
			</KioskProvider>
		);
	}

	if (tRPCError) {
		return (
			<KioskProvider isKiosk={isKiosk} startOverLookupId={startOverLookupId}>
				<CommonLayout>
					<ServerErrorAlert serverError={tRPCError} />
				</CommonLayout>
			</KioskProvider>
		);
	}

	if (lookupData?.settingId) {
		const settingIsServable = isSettingServableInP2({ settingId: lookupData.settingId, p2Location, p2Error });
		if (lookupData.tenantId && settingIsServable) {
			redirectToPortalV2({
				path: `location/${lookup}`,
				settingId: lookupData.settingId,
				tenantId: lookupData.tenantId,
			});
			return null;
		}
		return (
			<KioskProvider isKiosk={isKiosk} startOverLookupId={startOverLookupId}>
				<CommonLayout>
					<NotFoundAlert />
				</CommonLayout>
			</KioskProvider>
		);
	}

	if (lookupData?.permitType) {
		return (
			<KioskProvider isKiosk={isKiosk} startOverLookupId={startOverLookupId}>
				<PermitStoreProvider
					initialValues={{
						formData,
						wizardSettings: lookupData.permitType.wizardSettings,
						isParkingTimeStepForced: hasOverrideNotices,
					}}
				>
					<PermitFormLayout
						name={lookupData.selectedTenant?.name ?? lookupData.permitType.name}
						branding={lookupData.permitType.branding}
						companyName={lookupData.permitType.company.name}
						whiteLabel={lookupData.permitType.whiteLabel}
						support={lookupData.permitType.company.support}
						contact={lookupData.permitType.contact}
						locationId={lookup}
						tenantId={selectedTenantId ?? lookupData.selectedTenantId}
						location={lookupData.location}
						selectedTenant={lookupData.selectedTenant}
						selectedPermitType={{
							name: lookupData.permitType.name,
							isEvent: lookupData.permitType.isEvent,
						}}
						brandingUrl={lookupData.permitType.brandingUrl}
						venue={lookupData.permitType.venue}
						registrationInstructions={instructions ?? undefined}
						onChangeTenant={
							selectedTenantId
								? () => {
										setSelectedTenantId(undefined);
										setSelectedPermitTypeId(undefined);
									}
								: undefined
						}
						onChangePermitType={
							selectedPermitTypeId && hasMultipleParkingSelection
								? () => setSelectedPermitTypeId(undefined)
								: undefined
						}
					>
						{lookupData.permitType.status !== "active" ? (
							<>
								<PermitTypeUnavailable
									status={lookupData.permitType.status}
									choices={lookupData.permitType.wizardSettings.choices}
								/>

								{arrivedFromKioskReferrer && (
									<div className="flex justify-end p-4 md:p-8">
										<KioskStartOver />
									</div>
								)}
							</>
						) : (
							<CreatePermitWizard
								permitType={lookupData.permitType}
								overrideNotices={lookupData.overrideNotices}
								onSelectOverridePermitType={
									selectedPermitTypeId && !lookupData.permitTypeId
										? setSelectedPermitTypeId
										: undefined
								}
								preSelectedLocationId={lookupData.location?.id}
								entryLookupId={lookup}
								tenantId={selectedTenantId ?? lookupData.selectedTenantId}
								selectedTenant={lookupData.selectedTenant}
								registrationInstructions={instructions ?? undefined}
							/>
						)}
					</PermitFormLayout>
				</PermitStoreProvider>
			</KioskProvider>
		);
	}

	if (lookupData?.permitTypeId && !selectedTenantId && (lookupData.tenants?.length ?? 0) > 1) {
		return (
			<KioskProvider isKiosk={isKiosk} startOverLookupId={startOverLookupId}>
				<SelectionLayout
					location={lookupData.location}
					selectedTenant={lookupData.selectedTenant}
					branding={lookupData.branding}
					support={lookupData.support}
					contact={lookupData.contact}
					registrationInstructions={instructions ?? undefined}
				>
					<TenantSelection
						tenants={lookupData.tenants}
						onChange={(id) => {
							setSelectedTenantId(id);
							setSelectedPermitTypeId(lookupData.permitTypeId);
						}}
					/>
				</SelectionLayout>
			</KioskProvider>
		);
	}

	if (selectedTenantId || lookupData?.tenantId) {
		return (
			<KioskProvider isKiosk={isKiosk} startOverLookupId={startOverLookupId}>
				<SelectionLayout
					location={lookupData?.location}
					selectedTenant={lookupData?.selectedTenant}
					branding={lookupData?.branding}
					support={lookupData?.support}
					contact={lookupData?.contact}
					registrationInstructions={instructions ?? undefined}
					tenantId={effectiveTenantId}
					onChangeTenant={
						selectedTenantId
							? () => {
									setSelectedTenantId(undefined);
									setSelectedPermitTypeId(undefined);
								}
							: undefined
					}
				>
					<CombinedParkingOptionSelection
						permitTypes={lookupData?.permitTypes ?? []}
						timezone={lookupData?.location?.timezone}
						regSettings={allRegSettings}
						overrideNotices={lookupData?.overrideNotices}
						navOutRef={lookup}
						onSelectPermitType={(id) => {
							setSelectedPermitTypeId(id);
							setHasMultipleParkingSelection(totalPermitTypeAndRegSettingDisplayed > 1);
						}}
						onSelectRegSetting={(settingId) =>
							redirectToPortalV2({ path: `location/${lookup}`, settingId, tenantId: effectiveTenantId })
						}
					/>
				</SelectionLayout>
			</KioskProvider>
		);
	}

	const mergedTenants = mergeTenants({
		p3Tenants: lookupData?.tenants ?? [],
		p2Tenants: p2Location?.tenants ?? [],
		p2RegSettings: p2Location?.registrationSettings ?? [],
	});

	if (mergedTenants.length === 0) {
		return (
			<KioskProvider isKiosk={isKiosk} startOverLookupId={startOverLookupId}>
				<NotFoundAlert />
				{arrivedFromKioskReferrer && <KioskStartOver />}
			</KioskProvider>
		);
	}

	const tenantSelectionTenants = mergedTenants.map((tenant) => ({
		...tenant,
		discoverable: 1,
	}));

	return (
		<KioskProvider isKiosk={isKiosk} startOverLookupId={startOverLookupId}>
			<SelectionLayout
				location={lookupData?.location}
				selectedTenant={lookupData?.selectedTenant}
				branding={lookupData?.branding}
				support={lookupData?.support}
				contact={lookupData?.contact}
				registrationInstructions={instructions ?? undefined}
			>
				<TenantSelection tenants={tenantSelectionTenants} onChange={setSelectedTenantId} />
			</SelectionLayout>
		</KioskProvider>
	);
}
